// ── useAdminSettlement — 상태·이펙트·핸들러 훅 ───────────────────────────────
// PINE / LNJ / DOT 모두 이 훅을 공유.
// apiBase = ''    → /api/admin/settlement/...        (PINE·LNJ, companyCode 쿼리 사용)
// apiBase = 'dot' → /api/admin/settlement/dot/...    (DOT, companyCode 없음)

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast }  from '../../../components/Toast';
import { makeToast, sortAsc, buildLeftItems } from './utils';
import { useSettlementForm } from './useSettlementForm';
import { EMPTY_UNM_REV } from './constants';

/**
 * @param {string} companyCode - 정산 설정 기준 회사 코드 ('PINE' | 'LNJ' | 'DOT')
 * @param {string} apiBase     - API 경로 세그먼트 ('' = 공통, 'dot' = DOT 데이터/매출 전용)
 */
export function useAdminSettlement({ companyCode = null, apiBase = '' }) {
  const { showToast } = useToast();
  const toast = makeToast(showToast); // .success() / .error() 래퍼

  // 데이터/매출 API는 기존 분기 유지 (DOT은 별도 endpoint), 정산지급 기록은 공통
  const base    = apiBase ? `/${apiBase}` : '';
  const prefix  = `/api/admin/settlement${base}`;
  // 정산지급 기록 전용 (모든 회사 공통 endpoint)
  const recordsPrefix = '/api/admin/settlement';
  // companyCode 쿼리 파라미터 (항상 전달)
  const codeQS  = companyCode ? `&companyCode=${companyCode}` : '';

  /* 모바일 감지 (768px 이하) */
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [apiBase, companyCode, prefix]);

  /* 모바일 탭: 'stores' | 'timeline' | 'settlement' */
  const [mobileTab, setMobileTab] = useState('stores');

  /* 마케터 목록 */
  const [marketers, setMarketers] = useState([]);

  /* 선택된 마케터 */
  const [selectedUser, setSelectedUser] = useState(null); // { id, name }

  /* 좌+중 패널: 마케터 전체 정산 데이터 */
  const [marketerData, setMarketerData] = useState({
    clients: [],
    summary: { totalMinus: 0, totalPlus: 0, netTotal: 0 },
    unmatchedRevenue: EMPTY_UNM_REV,
    cancelEvents: [],
    consultantShareIncome: { items: [], total: 0 },
    consultantInfo: null,  // 상담자 배정 정보 + 배분율 (없으면 null)
  });
  const [dataLoading, setDataLoading] = useState(false);

  /* 미연결 결제 선택 — 결제 클릭 → selectedPay 설정 → 매장 클릭 → 연결 */
  const [selectedPay, setSelectedPay] = useState(null); // { type, id } | null
  const [linking,     setLinking]     = useState(false);

  /* 미연결 결제 연결 UI — 레거시 드롭다운 방식 (모바일 MbStoresTab에서 사용) */
  const [linkSelects, setLinkSelects] = useState({});

  /* 좌 패널 내부 탭: 'stores' | 'unmatched' */
  const [leftTab, setLeftTab] = useState('stores');

  /* 좌 패널: 펼쳐진 매장 ID */
  const [expandedId, setExpandedId] = useState(null);

  /* 우 패널: 정산 입금 기록 */
  const [records,    setRecords]    = useState([]);
  const [recLoading, setRecLoading] = useState(false);


  // ── 파생 값 계산 ───────────────────────────────────────────────────────────
  const { summary, unmatchedRevenue } = marketerData;
  const recTotal  = records.reduce((s, r) => s + Number(r.amount), 0);
  const unmTotal  = unmatchedRevenue.phones.length
    + unmatchedRevenue.deposits.length
    + unmatchedRevenue.cards.length
    + (unmatchedRevenue.cardUploads?.length || 0);
  const netExpected = summary.netTotal - recTotal;

  // 날짜순 타임라인 (중 패널 + 모바일 timeline 탭)
  // legacy admin timeline builder removed; PcCenterPanel uses the shared employee TimelineView.
  /*
    marketerData.clients,
    {
      card:        unmatchedRevenue.cards        || [],
      phone:       unmatchedRevenue.phones       || [],
      payments:    unmatchedRevenue.deposits     || [],
      cardUploads: unmatchedRevenue.cardUploads  || [], // DOT·LNJ 업로드 카드 포함
      others:      unmatchedRevenue.others       || [], // 기타 수입 (매출 관리 > 기타)
      consultantCharges: unmatchedRevenue.consultantCharges || [],
    },
    records,
  );
  */

  // 좌 패널: 매장카드 + 정산지급 + 기타 수입 날짜 내림차순 통합 목록
  const leftItems = buildLeftItems(marketerData.clients, records, marketerData.unmatchedRevenue);

  // ── 마케터 목록 로드 ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const qs  = !apiBase && companyCode ? `?companyCode=${companyCode}` : '';
      const res = await apiClient.get(`${prefix}/marketers${qs}`);
      if (res.ok) {
        const list = res.data.marketers || [];
        setMarketers(list);
        if (list.length === 1) setSelectedUser(list[0]); // 한 명이면 자동 선택
      }
    })();
  }, [apiBase, companyCode, prefix]);

  // ── 마케터 전체 정산 데이터 로드 ─────────────────────────────────────────
  const loadMarketerData = useCallback(async (userId) => {
    setDataLoading(true);
    try {
      const dataRes = await apiClient.get(
        `${prefix}/marketer-data?userId=${userId}${codeQS}`
      );
      if (dataRes.ok) {
        const unm = dataRes.data.unmatchedRevenue || EMPTY_UNM_REV;
        setMarketerData({
          clients: dataRes.data.clients || [],
          summary: dataRes.data.summary || { totalMinus: 0, totalPlus: 0, netTotal: 0 },
          cancelEvents: dataRes.data.cancelEvents || [],
          consultantShareIncome: dataRes.data.consultantShareIncome || { items: [], total: 0 },
          consultantInfo: dataRes.data.consultantInfo || null, // 상담자 배분율 정보
          // 미연결 결제 각 그룹 날짜 오름차순 정렬
          unmatchedRevenue: {
            ...unm,
            phones:      sortAsc(unm.phones,      (i) => i.paymentDate),
            deposits:    sortAsc(unm.deposits,     (i) => i.paymentDate),
            cards:       sortAsc(unm.cards,        (i) => i.transactionDate),
            cardUploads: sortAsc(unm.cardUploads,  (i) => i.transactionDate),
            consultantCharges: sortAsc(unm.consultantCharges, (i) => i.settlementDate || i.settlement_date),
          },
        });
      } else {
        setMarketerData({
          clients: [], summary: { totalMinus: 0, totalPlus: 0, netTotal: 0 },
          cancelEvents: [],
          consultantInfo: null, consultantShareIncome: { items: [], total: 0 }, unmatchedRevenue: EMPTY_UNM_REV,
        });
      }
      setSelectedPay(null);
      setLinkSelects({});
    } finally {
      setDataLoading(false);
    }
  }, [codeQS, prefix]);

  // ── 정산 입금 기록 로드 ───────────────────────────────────────────────────
  const loadRecords = useCallback(async (userId) => {
    setRecLoading(true);
    try {
      // companyCode 쿼리 포함 — PINE/lnj 구분
      const res = await apiClient.get(`${recordsPrefix}/records?userId=${userId}${codeQS}`);
      if (res.ok) setRecords(res.data.records || []);
      else { showToast(res.data?.message || '조회 실패', 'error'); setRecords([]); }
    } finally {
      setRecLoading(false);
    }
  }, [codeQS, recordsPrefix, showToast]);

  // ── 마케터 선택 시 전체 데이터 로드 ──────────────────────────────────────
  useEffect(() => {
    if (!selectedUser) {
      setMarketerData({
        clients: [],
        summary: { totalMinus: 0, totalPlus: 0, netTotal: 0 },
        unmatchedRevenue: EMPTY_UNM_REV,
        cancelEvents: [],
        consultantInfo: null,
        consultantShareIncome: { items: [], total: 0 },
      });
      setRecords([]);
      setExpandedId(null);
      setSelectedPay(null);
      setLinkSelects({});
      return;
    }
    loadMarketerData(selectedUser.id);
    loadRecords(selectedUser.id);
    resetForm();
  // resetForm is intentionally omitted here to avoid reloading settlement data when only form helpers are recreated.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, loadMarketerData, loadRecords]);

  // ── 미연결 결제 선택 토글 — 선택 후 담당매장 탭으로 자동 전환 ─────────────
  function handlePaymentClick({ type, id }) {
    const isSame = selectedPay?.type === type && selectedPay?.id === id;
    setSelectedPay(isSame ? null : { type, id });
    if (!isSame) setLeftTab('stores'); // 담당 매장 탭으로 자동 전환
  }

  // ── 매장 카드 클릭 → 선택된 결제 연결 (PC 방식) ─────────────────────────
  function handleStoreCardClick(clientId) {
    if (!selectedPay) return;
    handleLink(selectedPay.type, selectedPay.id, clientId);
  }

  // ── 미연결 결제 → 매장 연결 ────────────────────────────────────────────────
  // clientId 인수 있음: PC 방식 (handleStoreCardClick → 직접 전달)
  // clientId 인수 없음: 모바일 레거시 방식 (linkSelects에서 읽음)
  async function handleLink(type, id, clientId) {
    const cid = clientId ?? linkSelects[`${type}-${id}`];
    if (!cid) { toast.error('연결할 매장을 선택해 주세요.'); return; }
    setLinking(true);
    try {
      const res = await apiClient.patch(
        `${prefix}/revenue/${type}/${id}/link`,
        // companyCode 포함 — 백엔드에서 정확한 테이블 조회에 필요
        { clientId: Number(cid), userId: selectedUser.id, companyCode }
      );
      if (res.ok) {
        toast.success('연결 완료');
        setSelectedPay(null);
        await loadMarketerData(selectedUser.id);
      } else {
        toast.error(res.data?.message || '연결 실패');
      }
    } finally {
      setLinking(false);
    }
  }

  // ── 연결된 결제 → 매장 연결 해제 ─────────────────────────────────────────
  async function handleUnlink(type, id) {
    // companyCode 포함 — 공통 라우트에서 올바른 테이블 선택에 필요
    const res = await apiClient.patch(`${prefix}/revenue/${type}/${id}/unlink`, { companyCode });
    if (!res.ok) { toast.error(res.data?.message || '연결 해제 실패'); return; }
    toast.success('연결 해제됨');
    await loadMarketerData(selectedUser.id);
  }

  // ── 폼 상태 + 저장/삭제 핸들러 (useSettlementForm으로 분리) ──────────────
  const {
    editId, formDate, setFormDate, formAmt, formNote, setFormNote, saving,
    resetForm, selectRecord, onAmtChange, handleSave, handleDelete,
  } = useSettlementForm({ prefix: recordsPrefix, companyCode, apiBase, selectedUser, loadRecords });

  // ── 계약별 상담자 배분율 편집 모달 ────────────────────────────────────────
  // { contractId, currentRate, defaultRate } | null
  const [shareRateModal, setShareRateModal] = useState(null);

  // 편집 버튼 클릭 → 모달 오픈
  function handleEditShareRate(contractId, currentRate, defaultRate) {
    setShareRateModal({ contractId, currentRate, defaultRate });
  }

  // 저장 (오버라이드 값 적용)
  async function handleSaveShareRate(contractId, shareRate) {
    const res = await apiClient.patch(
      `/api/admin/contracts/${contractId}/consultant-share-rate`,
      { share_rate: shareRate }
    );
    if (!res.ok) { toast.error(res.data?.message || '저장 실패'); return; }
    toast.success('배분율이 저장됐습니다.');
    setShareRateModal(null);
    if (selectedUser) await loadMarketerData(selectedUser.id);
  }

  // 초기화 (오버라이드 해제 → 전역 기본값으로 복귀)
  async function handleResetShareRate(contractId) {
    const res = await apiClient.patch(
      `/api/admin/contracts/${contractId}/consultant-share-rate`,
      { share_rate: null }
    );
    if (!res.ok) { toast.error(res.data?.message || '초기화 실패'); return; }
    toast.success('기본값으로 초기화됐습니다.');
    setShareRateModal(null);
    if (selectedUser) await loadMarketerData(selectedUser.id);
  }

  return {
    // UI 상태
    isMobile, mobileTab, setMobileTab,
    // 마케터
    marketers, selectedUser, setSelectedUser,
    // 정산 데이터
    marketerData, dataLoading, summary, unmatchedRevenue,
    consultantInfo: marketerData.consultantInfo,  // 상담자 배분율 정보
    // 파생 값
    recTotal, unmTotal, netExpected, leftItems,
    // 좌 패널 탭
    leftTab, setLeftTab,
    // 연결 UI (PC — selectedPay 방식)
    selectedPay, setSelectedPay, handlePaymentClick, handleStoreCardClick, linking,
    // 연결 UI (모바일 레거시 — linkSelects 방식)
    linkSelects, setLinkSelects,
    // 카드 상태
    expandedId, setExpandedId,
    // 입금 기록
    records, recLoading,
    // 입력폼
    editId, formDate, setFormDate, formAmt, formNote, setFormNote, saving,
    // 핸들러
    handleLink, handleUnlink, resetForm, selectRecord, onAmtChange, handleSave, handleDelete,
    // 상담자 배분율 편집
    shareRateModal,
    onEditShareRate: handleEditShareRate,
    handleSaveShareRate,
    handleResetShareRate,
    closeShareRateModal: () => setShareRateModal(null),
  };
}
